import WelcomeView from '../views/Welcome.vue'
import HomeView from '../views/HomeView.vue'
import { ROUTES } from './routeNames'
import auth from './middleware/auth'
import guest from './middleware/guest'
import { useAuthStore } from '@/stores/authStore'
import { USER_ROLES } from '@/types'
import PaymentReturn from '@/views/payment/PaymentReturn.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import Login from '@/views/auth/Login.vue'
import Register from '@/views/auth/Register.vue'
import ForgotPassword from '@/views/auth/ForgotPassword.vue'
import ResetPassword from '@/views/auth/ResetPassword.vue'
import EmailVerification from '@/views/auth/EmailVerification.vue'
import AppLayout from '@/layouts/AppLayout.vue'
import DashboardRouter from '@/views/DashboardRouter.vue'
import ProfileShow from '@/views/profile/Show.vue'
import OrderIndex from '@/views/orders/Index.vue'
import OrderShow from '@/views/orders/Show.vue'
import BusinessIndex from '@/views/business/Index.vue'
import BusinessShow from '@/views/business/Show.vue'
import BusinessReviews from '@/views/business/Reviews.vue'
import BusinessEdit from '@/views/business/Edit.vue'
import ProductsIndex from '@/views/products/Index.vue'
import ProductsCreateOrEdit from '@/views/products/CreateOrEdit.vue'
import NotFound from '@/views/errors/NotFound.vue'
import AdminPayments from '@/views/admin/payments/Payments.vue'
import AdminPayment from '@/views/admin/payments/Payment.vue'
import AdminUsers from '@/views/admin/users/Users.vue'
import AdminUser from '@/views/admin/users/UserPage.vue'
import FeedbackIndex from '@/views/admin/feedback/Index.vue'
import FeedbackShow from '@/views/admin/feedback/Show.vue'
import SendNotifications from '@/views/admin/SendNotifications.vue'
import ProductShow from '@/views/products/Show.vue'
import NotificationsIndex from '@/views/notifications/Index.vue'
import NotificationsShow from '@/views/notifications/Show.vue'
import BusinessPaymentBreakdown from '@/views/admin/BusinessPaymentBreakdown.vue'
import Constants from '@/views/admin/Constants.vue'
import TermsAndConditions from '@/views/Terms.vue'
import PrivacyPolicy from '@/views/Privacy.vue'
const cantSeeIfBusiness = (to: any, from: any, next: any) => {
  const authStore = useAuthStore()
  if (authStore.user?.role === USER_ROLES.BUSINESS) {
    next({ name: ROUTES.DASHBOARD })
  } else {
    next()
  }
}
const cantSeeIfCustomer = (to: any, from: any, next: any) => {
  const authStore = useAuthStore()
  if (authStore.user?.role === USER_ROLES.CUSTOMER) {
    next({ name: ROUTES.DASHBOARD })
  } else {
    next()
  }
}

const cantSeeIfNotAdmin = (to: any, from: any, next: any) => {
  const authStore = useAuthStore()
  if (authStore.user?.role !== USER_ROLES.ADMIN) {
    next({ name: ROUTES.DASHBOARD })
  } else {
    next()
  }
}

export const routes = [
  {
    path: '/',
    name: ROUTES.HOME,
    component: WelcomeView,
    beforeEnter: (to: any, from: any, next: any) => {
      const authStore = useAuthStore()
      authStore.getUser()
      next()
    },
  },
  {
    path: '/payment/return',
    name: ROUTES.PAYMENT_RETURN,
    component: PaymentReturn,
    meta: { standalone: true }, // This route doesn't need the standard layout
  },
  {
    path: '/auth',
    name: ROUTES.AUTH,
    component: AuthLayout,
    meta: { middleware: [guest] },
    redirect: { name: ROUTES.LOGIN },
    children: [
      {
        path: 'login',
        name: ROUTES.LOGIN,
        component: Login,
      },
      {
        path: 'register',
        name: ROUTES.REGISTER,
        component: Register,
      },
      {
        path: 'forgot-password',
        name: ROUTES.FORGOTPASSWORD,
        component: ForgotPassword,
      },
      {
        path: 'reset-password',
        name: ROUTES.RESETPASSWORD,
        component: ResetPassword,
        props: (route: any) => ({ token: route.params.token, email: route.query.email }),
      },
      {
        path: 'verify-email',
        name: ROUTES.EMAIL_VERIFICATION,
        component: EmailVerification,
        meta: { middleware: [auth] },
      },
    ],
  },
  {
    path: '/dashboard',
    name: ROUTES.APP,
    component: AppLayout,
    meta: { middleware: [auth] },
    redirect: { name: ROUTES.DASHBOARD },
    children: [
      {
        path: '',
        name: ROUTES.DASHBOARD,
        component: DashboardRouter,
        meta: {
          title: 'Dashboard',
          dynamicTitle: true, // Flag to indicate title should be updated based on role
        },
      },
      {
        path: 'profile',
        name: ROUTES.PROFILE,
        component: ProfileShow,
      },
      {
        path: 'orders',
        name: ROUTES.DASHBOARD_ORDERS,
        component: OrderIndex,
        meta: { title: 'Orders' },
      },
      {
        path: 'orders/:id',
        name: ROUTES.DASHBOARD_ORDER,
        component: OrderShow,
        meta: { title: 'Order Details' },
      },
      {
        path: 'orders/:id/payment-breakdown',
        name: ROUTES.ADMIN_BUSINESS_PAYMENT_BREAKDOWN,
        component: BusinessPaymentBreakdown,
        beforeEnter: cantSeeIfNotAdmin,
        meta: { title: 'Payment Breakdown' },
      },
      {
        path: 'businesses',
        name: ROUTES.BUSINESSES,
        component: BusinessIndex,
        meta: { title: 'Businesses' },
        beforeEnter: cantSeeIfBusiness,
      },
      {
        path: 'businesses/:id',
        name: ROUTES.BUSINESS,
        component: BusinessShow,
        meta: { title: 'Business Details' },
        beforeEnter: cantSeeIfBusiness,
      },
      {
        path: 'business/:id/reviews',
        name: ROUTES.BUSINESS_REVIEW,
        component: BusinessReviews,
        meta: { title: 'Business Reviews' },
      },
      {
        path: 'business/:id/edit',
        name: ROUTES.BUSINESS_EDIT,
        component: BusinessEdit,
        meta: { title: 'Business Edit' },
      },
      {
        path: 'products',
        name: ROUTES.PRODUCTS,
        component: ProductsIndex,
        meta: { title: 'Products' },
      },
      {
        path: 'constants',
        name: ROUTES.ADMIN_CONSTANTS,
        component: Constants,
        meta: { title: 'Constants' },
        beforeEnter: cantSeeIfNotAdmin,
      },
      {
        path: 'products/create',
        name: ROUTES.PRODUCT_CREATE,
        component: ProductsCreateOrEdit,
        meta: { title: 'Create Product' },
      },
      {
        path: 'products/:id',
        name: ROUTES.PRODUCT,
        component: ProductShow,
        meta: { title: 'Product Details' },
      },
      {
        path: 'products/:id/edit',
        name: ROUTES.PRODUCT_EDIT,
        component: ProductsCreateOrEdit,
        meta: { title: 'Edit Product' },
      },
      {
        path: 'notifications',
        name: ROUTES.NOTIFICATIONS,
        component: NotificationsIndex,
        meta: { title: 'Notifications' },
      },
      {
        path: 'notifications/:id',
        name: ROUTES.NOTIFICATION,
        component: NotificationsShow,
        meta: { title: 'Notification' },
      },
      {
        path: 'pays',
        beforeEnter: cantSeeIfNotAdmin,
        children: [
          {
            path: '',
            name: ROUTES.ADMIN_PAYMENTS,
            component: AdminPayments,
            meta: { title: 'Payments' },
          },
          {
            path: ':id',
            name: ROUTES.ADMIN_PAYMENT,
            component: AdminPayment,
            meta: { title: 'Payment' },
          },
        ],
      },
      {
        path: 'users',
        beforeEnter: cantSeeIfNotAdmin,
        children: [
          {
            path: '',
            name: ROUTES.ADMIN_USERS,
            component: AdminUsers,
            meta: { title: 'Users' },
          },
          {
            path: ':id',
            name: ROUTES.ADMIN_USER,
            component: AdminUser,
            meta: { title: 'User' },
          },
        ],
      },
      {
        path: 'feedbacks',
        beforeEnter: cantSeeIfNotAdmin,
        children: [
          {
            path: '',
            name: ROUTES.FEEDBACKS,
            component: FeedbackIndex,
            meta: { title: 'Feedbacks' },
          },
          {
            path: ':id',
            name: ROUTES.FEEDBACK,
            component: FeedbackShow,
            meta: { title: 'Feedback' },
          },
        ],
      },
      {
        path: 'send-notifications',
        name: ROUTES.ADMIN_SEND_NOTIFICATIONS,
        component: SendNotifications,
        meta: { title: 'Send Notifications' },
        beforeEnter:  cantSeeIfNotAdmin,
      },
    ],
  },
  {
    path: '/terms-and-conditions',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: ROUTES.TERMS_AND_CONDITIONS,
        component: TermsAndConditions,
        meta: { title: 'Terms and Conditions' },
      },
    ],
  },
  {
    path: '/privacy-policy',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: ROUTES.PRIVACY_POLICY,
        component: PrivacyPolicy,
        meta: { title: 'Privacy Policy' },
      },
    ],
  },
  {
    path: '/businesses',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: ROUTES.PUBLIC_BUSINESSES,
        component: BusinessIndex,
        meta: { title: 'Businesses' },
      },
      {
        path: ':id',
        name: ROUTES.PUBLIC_BUSINESS,
        component: BusinessShow,
        meta: { title: 'Business' },
      },
      {
        path: ':id/reviews',
        name: ROUTES.PUBLIC_BUSINESS_REVIEW,
        component: BusinessReviews,
        meta: { title: 'Business Reviews' },
      },
    ],
  },

  {
    name: ROUTES.LOGOUT,
    path: '/logout',
    beforeEnter: async (_: any, __: any, next: any) => {
      try {
        const authStore = useAuthStore()
        await authStore.logout()
        // Redirect happens in the logout function after successful logout
        next()
      } catch (error) {
        // If there was an error during logout, redirect to login page
        console.error('Error during logout:', error)
        next({ name: ROUTES.LOGIN })
      }
    },
  },
  // 404 page - catch all route
  {
    path: '/:pathMatch(.*)*',
    name: ROUTES.NOT_FOUND,
    component: NotFound,
    meta: { title: 'Page Not Found' },
  },
]
